import { extendTheme, type ThemeConfig, withDefaultColorScheme } from '@/ui';
import { colors } from './colors';
import * as components from './components';
import { layerStyles } from './layerStyles';
import { textStyles } from './textStyles';

const fonts = {
  heading: 'BasisGrotesquePro, sans-serif',
  body: 'BasisGrotesquePro, sans-serif',
  display: 'CooperBT, system-ui, -apple-system, sans-serif',
};

const breakpoints = {
  xs: '450px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  '2xl': '1536px',
  '3xl': '1920px',
  'magic-max-width': '1400px',
};

// Shared resting width of the two sliding side rails (Magic panel + customize
// sidebar). Exported as a number because both animate it with framer-motion.
export const AH_RAIL_WIDTH = 308;

const sizes = {
  10: '42px',
  container: {
    xl: '1352px',
  },
  'ah-header': '57px',
  'ah-nav': '42px',
  'ah-nav-lg': '60px',
  'ah-bar': '52px',
  'ah-rail': `${AH_RAIL_WIDTH}px`,
};

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

// ah-prefixed to avoid colliding with Chakra's built-in md/lg/sm/xl keys.
const radii = {
  'ah-md': '8px',
  'ah-lg': '10px',
  'ah-xl': '14px',
  'ah-2xl': '16px',
  'ah-panel': '18px',
  'ah-pill': '100px',
};

const fontSizes = {
  'ah-micro': '10px',
  'ah-label': '11px',
  'ah-control': '13px',
  'ah-row': '13.5px',
  'ah-status': '12px',
  'ah-display': '26px',
};

const letterSpacings = {
  'ah-label': '0.09em',
};

// Brand-colored shadows built on magicPurple.500 (#5D3FF6 / rgb(93,63,246)).
const shadows = {
  'ah-ring-selected': '0 0 0 3px rgba(93, 63, 246, 0.13)',
  'ah-ring-active': '0 0 0 2px rgba(93, 63, 246, 0.18)',
  // The lifted-surface shadow shared by the interactive month/week grid card and
  // the list event card, so the two can't drift apart: a hairline contact shadow
  // plus a wide, soft ambient one. Note the -24px spread means it only renders
  // on a reasonably tall surface, not on a ~33px grid event card.
  'ah-card-raised':
    '0 1px 2px rgba(0,0,0,0.04), 0 24px 60px -24px rgba(0,0,0,0.14)',
};

// Defensive backstop so a single overflowing descendant (e.g. a bar that can't
// shrink on a narrow viewport) can't scroll the whole page horizontally.
// Use `clip` rather than `hidden`: `hidden` makes body a scroll container, which
// activates the `scrollbar-gutter: stable` on body (see index.css) and reserves a
// 15px gutter that shrinks the app column and leaves the page bg showing beside
// the header. `clip` prevents horizontal scroll without becoming a scroll container.
const styles = {
  global: {
    'html, body': {
      overflowX: 'clip',
    },
  },
};
const theme = extendTheme(
  {
    sizes,
    fonts,
    colors,
    radii,
    fontSizes,
    letterSpacings,
    shadows,
    components: { ...components }, // Convert import collection to plain object
    breakpoints,
    layerStyles,
    textStyles,
    styles,
    config,
    // https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/semantic-tokens.ts
    semanticTokens: {
      lineHeights: {
        base: 1.4,
      },
      colors: {
        'chakra-body-text': {
          _light: 'customgray.textdark',
        },
      },
    },
  },
  withDefaultColorScheme({ colorScheme: 'brand' })
);

export { colors, components, theme, breakpoints };
